import { onAuthStateChanged, signInAnonymously, type User } from 'firebase/auth';
import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { auth, db } from './firebase';

// Capacitor on iOS uses a custom URL scheme (Tessera://...) for the local web
// bundle. Under that scheme, Firebase Auth's onAuthStateChanged listener can
// silently stall on its very first invocation — IndexedDB persistence reads
// hang without throwing, so neither the success path nor the error path
// fires. The result is the React app sitting forever on the "Preparing..."
// loading view (App.tsx).
//
// To recover, we race the Firebase init promise against a hard timeout. If
// auth doesn't settle in BOOTSTRAP_TIMEOUT_MS, reject — App.tsx's .catch
// already routes that to setView('picker'), so the player still gets into
// the game in offline mode (cloud sync no-ops until the next session that
// gets a clean handshake). 6 seconds is generous against typical Firebase
// auth round-trips (~300-800ms on cellular) while staying short enough to
// keep the cold-start UX from feeling broken.
const BOOTSTRAP_TIMEOUT_MS = 6000;

export async function bootstrapAuth(): Promise<User> {
  return new Promise((resolve, reject) => {
    let settled = false;
    const settleResolve = (user: User) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      try { unsub(); } catch { /* unsub may not be defined yet on sync settle */ }
      resolve(user);
    };
    const settleReject = (err: unknown) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      try { unsub(); } catch { /* same — guard sync rejects */ }
      reject(err);
    };

    const timeoutId = setTimeout(() => {
      settleReject(new Error(
        `bootstrapAuth: Firebase Auth did not settle within ${BOOTSTRAP_TIMEOUT_MS}ms ` +
        `(likely Capacitor + iOS persistence stall). Falling through to offline mode.`,
      ));
    }, BOOTSTRAP_TIMEOUT_MS);

    const unsub = onAuthStateChanged(auth, async (user) => {
      try {
        if (user) {
          await ensureUserDoc(user);
          settleResolve(user);
        } else {
          const cred = await signInAnonymously(auth);
          await ensureUserDoc(cred.user);
          settleResolve(cred.user);
        }
      } catch (e) {
        settleReject(e);
      }
    });
  });
}

async function ensureUserDoc(user: User): Promise<void> {
  const ref = doc(db, 'users', user.uid);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    await setDoc(ref, {
      displayName: user.displayName ?? null,
      locale: navigator.language ?? null,
      createdAt: serverTimestamp(),
    });
  }
}
